import type { TimeDistributionDto } from "@normax-performance/shared-types";
import { formatSharePercent } from "@normax-performance/shared-format";
import { formatAnalyticsMinutes } from "../../lib/format";
import "./TimeDistributionBar.css";

type Segment = "normed" | "neutral" | "unnormed";

export interface TimeDistributionBarProps {
  distribution: TimeDistributionDto;
  /** Localized segment labels (e.g. "Normirani rad" / "Neutralne aktivnosti" / "Rad bez norme"). */
  labels: Record<Segment, string>;
  intlLocale: string;
  /**
   * `"full"` — bar + a legend row per segment with minutes + one-decimal %
   * (the authoritative summary presentation). `"compact"` — a thin bar + one
   * inline integer-% legend line, for a dense table cell.
   */
  variant?: "full" | "compact";
  /** Screen-reader / title prefix, e.g. "Raspodela evidentiranog vremena". */
  ariaLabel?: string;
}

const ORDER: Segment[] = ["normed", "neutral", "unnormed"];

/**
 * The one shared "where did the recorded time go" visual — a segmented
 * Normirani rad / Neutralne aktivnosti / Rad bez norme bar, used by the
 * Reports summary, Analytics summary + workers table, and the Dashboard
 * (task: time-distribution simplification §14). Pure presentation: every
 * value arrives pre-computed on `TimeDistributionDto` and is only formatted
 * here, through the SAME `formatSharePercent` rule the PDF uses. Deliberately
 * NOT a performance widget — performance / Method C ("Učinak") is its own
 * separate concept and never appears here.
 */
export function TimeDistributionBar({
  distribution,
  labels,
  intlLocale,
  variant = "full",
  ariaLabel,
}: TimeDistributionBarProps) {
  const shares: Record<Segment, number | null> = {
    normed: distribution.normedShare,
    neutral: distribution.neutralShare,
    unnormed: distribution.unnormedShare,
  };
  const minutes: Record<Segment, string> = {
    normed: distribution.normedMinutes,
    neutral: distribution.neutralMinutes,
    unnormed: distribution.unnormedMinutes,
  };
  const hasData = distribution.normedShare !== null;

  const bar = (
    <div
      className={`nx-time-dist__bar nx-time-dist__bar--${variant}`}
      role="img"
      aria-label={
        ariaLabel
          ? `${ariaLabel}: ${ORDER.map((s) => `${labels[s]} ${formatSharePercent(shares[s], variant === "compact" ? 0 : 1)}`).join(", ")}`
          : undefined
      }
    >
      {hasData ? (
        ORDER.filter((s) => (shares[s] ?? 0) > 0).map((s) => (
          <span
            key={s}
            className={`nx-time-dist__segment nx-time-dist__segment--${s}`}
            style={{ width: `${(shares[s] ?? 0) * 100}%` }}
          />
        ))
      ) : (
        <span className="nx-time-dist__segment nx-time-dist__segment--empty" style={{ width: "100%" }} />
      )}
    </div>
  );

  if (variant === "compact") {
    return (
      <div className="nx-time-dist nx-time-dist--compact">
        {bar}
        <p className="nx-time-dist__inline-legend">
          {hasData
            ? ORDER.map((s) => `${labels[s]} ${formatSharePercent(shares[s], 0)}`).join(" · ")
            : "—"}
        </p>
      </div>
    );
  }

  return (
    <div className="nx-time-dist">
      {bar}
      <ul className="nx-time-dist__legend">
        {ORDER.map((s) => (
          <li key={s} className="nx-time-dist__legend-item">
            <span className={`nx-time-dist__swatch nx-time-dist__swatch--${s}`} aria-hidden="true" />
            <span className="nx-time-dist__legend-label">{labels[s]}</span>
            <span className="nx-time-dist__legend-value">
              {formatAnalyticsMinutes(minutes[s], intlLocale)} min
              <span className="nx-time-dist__legend-share"> · {formatSharePercent(shares[s], 1)}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
